import React, { useState, useEffect, useCallback } from "react";
import { Popconfirm, message, Spin, Tooltip, Switch } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  LinkOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  AptReferral,
  ReferralReward,
  ReferralRewardType,
  ReferralThresholdType,
  getAptReferrals,
  createAptReferral,
  updateAptReferral,
  deleteAptReferral,
} from "../../../utils/api/apartmentRentalsAPI";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const slugify = (s: string) =>
  s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").slice(0, 40);

const rewardTypeLabel = (type: ReferralRewardType) =>
  type === "discount" ? "Popust %" : "Besplatne noći";

const thresholdTypeLabel = (t: ReferralThresholdType) =>
  t === "bookings" ? "rezervacija" : "noći";

const rewardIcon = (type: ReferralRewardType) =>
  type === "discount" ? "🏷" : "🌙";

const rewardValueLabel = (r: ReferralReward) =>
  r.type === "discount" ? `${r.value}% popust` : `${r.value} besplatnih noći`;

const thresholdProgress = (r: ReferralReward, totalBookings: number, totalNights: number) => {
  const current = r.thresholdType === "bookings" ? totalBookings : totalNights;
  const pct = Math.min(100, Math.round((current / r.threshold) * 100));
  return { current, pct, unlocked: current >= r.threshold };
};

const emptyReward = (): ReferralReward => ({
  type: "discount",
  thresholdType: "bookings",
  threshold: 10,
  value: 5,
  label: "",
});

// ─── Reward Editor Row ────────────────────────────────────────────────────────

interface RewardRowProps {
  reward: ReferralReward;
  index: number;
  onChange: (i: number, r: ReferralReward) => void;
  onRemove: (i: number) => void;
}

const RewardRow: React.FC<RewardRowProps> = ({ reward, index, onChange, onRemove }) => {
  const set = (patch: Partial<ReferralReward>) => onChange(index, { ...reward, ...patch });

  return (
    <div className="ar-rw-editor-row">
      <div className="ar-rw-editor-row-top">
        {/* Reward type */}
        <div className="ar-rw-field">
          <label>Nagrada</label>
          <select value={reward.type} onChange={(e) => set({ type: e.target.value as ReferralRewardType })}>
            <option value="discount">🏷 Popust %</option>
            <option value="free_nights">🌙 Besplatne noći</option>
          </select>
        </div>

        {/* Value */}
        <div className="ar-rw-field ar-rw-field--sm">
          <label>{reward.type === "discount" ? "Procenat" : "Broj noći"}</label>
          <div className="ar-rw-num-wrap">
            <input
              type="number"
              min={1}
              max={reward.type === "discount" ? 100 : 365}
              value={reward.value}
              onChange={(e) => set({ value: Math.max(1, Number(e.target.value)) })}
            />
            <span className="ar-rw-num-unit">{reward.type === "discount" ? "%" : "noći"}</span>
          </div>
        </div>

        {/* Threshold type */}
        <div className="ar-rw-field">
          <label>Uslov — mjeri se po</label>
          <select
            value={reward.thresholdType}
            onChange={(e) => set({ thresholdType: e.target.value as ReferralThresholdType })}
          >
            <option value="bookings">Broj rezervacija</option>
            <option value="nights">Broj noći</option>
          </select>
        </div>

        {/* Threshold value */}
        <div className="ar-rw-field ar-rw-field--sm">
          <label>Minimum</label>
          <div className="ar-rw-num-wrap">
            <input
              type="number"
              min={1}
              value={reward.threshold}
              onChange={(e) => set({ threshold: Math.max(1, Number(e.target.value)) })}
            />
            <span className="ar-rw-num-unit">{thresholdTypeLabel(reward.thresholdType)}</span>
          </div>
        </div>

        <button className="ar-rw-remove" onClick={() => onRemove(index)} title="Ukloni nagradu">
          <CloseOutlined />
        </button>
      </div>

      {/* Optional label */}
      <div className="ar-rw-field" style={{ marginTop: 6 }}>
        <label>Opis nagrade (opcionalno)</label>
        <input
          placeholder={`npr. "${rewardValueLabel(reward)} od ${reward.threshold} ${thresholdTypeLabel(reward.thresholdType)}"`}
          value={reward.label || ""}
          onChange={(e) => set({ label: e.target.value })}
        />
      </div>

      <div className="ar-rw-summary">
        {rewardIcon(reward.type)}{" "}
        <strong>{rewardValueLabel(reward)}</strong>{" "}
        nakon <strong>{reward.threshold} {thresholdTypeLabel(reward.thresholdType)}</strong>
        {reward.label ? ` — ${reward.label}` : ""}
      </div>
    </div>
  );
};

// ─── Referral Form (create or edit) ──────────────────────────────────────────

interface RefFormState {
  name: string;
  code: string;
  description: string;
  rewards: ReferralReward[];
}

interface RefFormProps {
  initial?: Partial<RefFormState>;
  codeReadonly?: boolean;
  saving: boolean;
  onSave: (data: RefFormState) => void;
  onCancel: () => void;
  publicBase: string;
  title: string;
  submitLabel: string;
}

const RefForm: React.FC<RefFormProps> = ({
  initial, codeReadonly, saving, onSave, onCancel, publicBase, title, submitLabel,
}) => {
  const [form, setForm] = useState<RefFormState>({
    name: initial?.name || "",
    code: initial?.code || "",
    description: initial?.description || "",
    rewards: initial?.rewards || [],
  });
  const [codeEdited, setCodeEdited] = useState(codeReadonly || !!initial?.code);

  const setField = (patch: Partial<RefFormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleNameChange = (name: string) => {
    setForm((f) => ({
      ...f,
      name,
      code: (codeEdited || codeReadonly) ? f.code : slugify(name),
    }));
  };

  const handleCodeChange = (code: string) => {
    setCodeEdited(true);
    setField({ code: code.toLowerCase().replace(/[^a-z0-9-]/g, "") });
  };

  const addReward = () => setField({ rewards: [...form.rewards, emptyReward()] });

  const updateReward = (i: number, r: ReferralReward) => {
    const next = [...form.rewards];
    next[i] = r;
    setField({ rewards: next });
  };

  const removeReward = (i: number) =>
    setField({ rewards: form.rewards.filter((_, idx) => idx !== i) });

  return (
    <div className="ar-ref-form">
      <div className="ar-ref-form-title">{title}</div>

      {/* Basic fields */}
      <div className="ar-ref-form-row">
        <div className="ar-ref-form-field">
          <label>Naziv <span style={{ color: "#dc2626" }}>*</span></label>
          <input
            placeholder="npr. Marija Instagram"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
          />
        </div>
        <div className="ar-ref-form-field">
          <label>Kod (slug) <span style={{ color: "#dc2626" }}>*</span></label>
          <div className="ar-ref-slug-wrap">
            <span className="ar-ref-slug-prefix">?ref=</span>
            <input
              placeholder="marija2026"
              value={form.code}
              readOnly={codeReadonly}
              style={codeReadonly ? { opacity: 0.6 } : {}}
              onChange={(e) => handleCodeChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="ar-ref-form-field">
        <label>Opis (opcionalno)</label>
        <input
          placeholder="npr. Instagram promocija Jun 2026"
          value={form.description}
          onChange={(e) => setField({ description: e.target.value })}
        />
      </div>

      {form.code && (
        <div className="ar-ref-preview-url">
          <LinkOutlined style={{ marginRight: 6, opacity: 0.6 }} />
          {publicBase}&ref={form.code}
        </div>
      )}

      {/* Rewards section */}
      <div className="ar-rw-section">
        <div className="ar-rw-section-head">
          <div>
            <div className="ar-rw-section-title">Nagrade za referera</div>
            <div className="ar-rw-section-sub">
              Šta osoba koja dijeli ovaj link dobija od vas — popust ili besplatne noći, ovisno o broju postignutih rezervacija ili noći.
            </div>
          </div>
          <button className="ar-btn ar-btn--ghost ar-rw-add-btn" onClick={addReward}>
            <PlusOutlined /> Dodaj nagradu
          </button>
        </div>

        {form.rewards.length === 0 ? (
          <div className="ar-rw-empty">
            Nema dodijeljenih nagrada — kliknite "Dodaj nagradu" da dodate uvjet za nagradu.
          </div>
        ) : (
          <div className="ar-rw-editor-list">
            {form.rewards.map((r, i) => (
              <RewardRow key={i} reward={r} index={i} onChange={updateReward} onRemove={removeReward} />
            ))}
          </div>
        )}
      </div>

      <div className="ar-ref-form-actions">
        <button className="ar-btn ar-btn--ghost" onClick={onCancel}>
          Otkaži
        </button>
        <button className="ar-btn ar-btn--primary" onClick={() => onSave(form)} disabled={saving}>
          {saving ? "Čuvanje..." : submitLabel}
        </button>
      </div>
    </div>
  );
};

// ─── Reward Progress Card ─────────────────────────────────────────────────────

const RewardProgress: React.FC<{
  reward: ReferralReward;
  totalBookings: number;
  totalNights: number;
}> = ({ reward, totalBookings, totalNights }) => {
  const { current, pct, unlocked } = thresholdProgress(reward, totalBookings, totalNights);
  const label = reward.label || `${rewardValueLabel(reward)} — min. ${reward.threshold} ${thresholdTypeLabel(reward.thresholdType)}`;

  return (
    <div className={`ar-rw-progress${unlocked ? " ar-rw-progress--unlocked" : ""}`}>
      <div className="ar-rw-progress-top">
        <span className="ar-rw-progress-icon">{rewardIcon(reward.type)}</span>
        <div className="ar-rw-progress-info">
          <div className="ar-rw-progress-label">{label}</div>
          <div className="ar-rw-progress-meta">
            {unlocked ? (
              <span className="ar-rw-badge ar-rw-badge--unlocked">✓ Postignuto</span>
            ) : (
              <span className="ar-rw-badge ar-rw-badge--pending">
                {current} / {reward.threshold} {thresholdTypeLabel(reward.thresholdType)}
              </span>
            )}
          </div>
        </div>
        <div className="ar-rw-progress-value">{rewardValueLabel(reward)}</div>
      </div>
      <div className="ar-rw-bar-track">
        <div
          className={`ar-rw-bar-fill${unlocked ? " ar-rw-bar-fill--unlocked" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  clientId: string;
}

const ReferralsTab: React.FC<Props> = ({ clientId }) => {
  const [referrals, setReferrals] = useState<AptReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const publicBase = `${window.location.origin}/apartments/public?clientId=${clientId}`;

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await getAptReferrals(clientId);
      setReferrals(res.data);
    } catch {
      message.error("Greška pri učitavanju referala");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data: { name: string; code: string; description: string; rewards: ReferralReward[] }) => {
    if (!data.name.trim() || !data.code.trim()) {
      message.warning("Naziv i kod su obavezni");
      return;
    }
    setSaving(true);
    try {
      await createAptReferral(clientId, data);
      message.success("Referral link kreiran");
      setShowCreateForm(false);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Greška pri kreiranju");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (
    id: string,
    data: { name: string; code: string; description: string; rewards: ReferralReward[] }
  ) => {
    setSaving(true);
    try {
      await updateAptReferral(id, { name: data.name, description: data.description, rewards: data.rewards });
      message.success("Referral ažuriran");
      setEditingId(null);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Greška pri ažuriranju");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (ref: AptReferral, isActive: boolean) => {
    try {
      await updateAptReferral(ref._id, { isActive });
      setReferrals((prev) => prev.map((r) => (r._id === ref._id ? { ...r, isActive } : r)));
    } catch {
      message.error("Greška pri ažuriranju");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAptReferral(id);
      message.success("Referral izbrisan");
      load();
    } catch {
      message.error("Greška pri brisanju");
    }
  };

  const copyLink = (ref: AptReferral) => {
    navigator.clipboard.writeText(`${publicBase}&ref=${ref.code}`);
    message.success(`Link za "${ref.name}" kopiran!`);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="ar-ref-page">
      {/* Header */}
      <div className="ar-ref-header">
        <div>
          <div className="ar-ref-header-title">Referral linkovi</div>
          <div className="ar-ref-header-sub">
            Personalizovani linkovi za agente, influensere ili partnere. Pratite rezervacije po izvoru i dodijelite nagrade.
          </div>
        </div>
        {!showCreateForm && (
          <button className="ar-btn ar-btn--primary" onClick={() => setShowCreateForm(true)}>
            <PlusOutlined /> Novi referral
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreateForm && (
        <RefForm
          publicBase={publicBase}
          title="Novi referral link"
          submitLabel="Kreiraj link"
          saving={saving}
          onSave={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* List */}
      {referrals.length === 0 && !showCreateForm ? (
        <div className="ar-empty">
          <div style={{ fontSize: 42, opacity: 0.4 }}>🔗</div>
          <div className="t">Nema referral linkova</div>
          <div className="s">Kreirajte linkove za agente, influensere ili partnere.</div>
          <button className="ar-btn ar-btn--primary" style={{ marginTop: 16 }} onClick={() => setShowCreateForm(true)}>
            <PlusOutlined /> Kreirati prvi referral
          </button>
        </div>
      ) : (
        <div className="ar-ref-list">
          {referrals.map((ref) => (
            <div key={ref._id} className={`ar-ref-card${ref.isActive ? "" : " ar-ref-card--inactive"}`}>
              {/* Edit form (inline) */}
              {editingId === ref._id ? (
                <RefForm
                  publicBase={publicBase}
                  title={`Izmijeni: ${ref.name}`}
                  submitLabel="Sačuvaj izmjene"
                  saving={saving}
                  codeReadonly
                  initial={{
                    name: ref.name,
                    code: ref.code,
                    description: ref.description || "",
                    rewards: ref.rewards || [],
                  }}
                  onSave={(data) => handleUpdate(ref._id, data)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <>
                  {/* Card top */}
                  <div className="ar-ref-card-top">
                    <div className="ar-ref-card-info">
                      <div className="ar-ref-name">{ref.name}</div>
                      {ref.description && <div className="ar-ref-desc">{ref.description}</div>}
                      <div className="ar-ref-url">
                        <span className="ar-ref-url-text">{publicBase}&ref={ref.code}</span>
                      </div>
                    </div>
                    <div className="ar-ref-card-actions">
                      <Tooltip title={ref.isActive ? "Deaktiviraj" : "Aktiviraj"}>
                        <Switch size="small" checked={ref.isActive} onChange={(v) => handleToggle(ref, v)} />
                      </Tooltip>
                      <Tooltip title="Kopiraj link">
                        <button
                          className="ar-btn ar-btn--ghost"
                          style={{ width: 32, padding: 0, justifyContent: "center" }}
                          onClick={() => copyLink(ref)}
                        >
                          <CopyOutlined style={{ fontSize: 13 }} />
                        </button>
                      </Tooltip>
                      <Tooltip title="Izmijeni nagrade i podatke">
                        <button
                          className="ar-btn ar-btn--ghost"
                          style={{ width: 32, padding: 0, justifyContent: "center" }}
                          onClick={() => setEditingId(ref._id)}
                        >
                          <EditOutlined style={{ fontSize: 13 }} />
                        </button>
                      </Tooltip>
                      <Popconfirm
                        title="Izbriši referral link?"
                        description="Historija rezervacija ostaje sačuvana."
                        onConfirm={() => handleDelete(ref._id)}
                        okText="Da"
                        cancelText="Ne"
                        okButtonProps={{ danger: true }}
                      >
                        <Tooltip title="Izbriši">
                          <button
                            className="ar-btn ar-btn--ghost"
                            style={{ width: 32, padding: 0, justifyContent: "center", color: "#dc2626" }}
                          >
                            <DeleteOutlined style={{ fontSize: 13 }} />
                          </button>
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="ar-ref-stats">
                    <div className="ar-ref-stat">
                      <div className="ar-ref-stat-val">{ref.totalBookings}</div>
                      <div className="ar-ref-stat-lab">Rezervacija</div>
                    </div>
                    <div className="ar-ref-stat">
                      <div className="ar-ref-stat-val">{ref.totalNights || 0}</div>
                      <div className="ar-ref-stat-lab">Noći ukupno</div>
                    </div>
                    <div className="ar-ref-stat">
                      <div className="ar-ref-stat-val">
                        {ref.totalRevenue > 0 ? new Intl.NumberFormat("sr-RS").format(ref.totalRevenue) : "—"}
                      </div>
                      <div className="ar-ref-stat-lab">Prihod</div>
                    </div>
                    <div className="ar-ref-stat">
                      <div className={`ar-ref-badge ${ref.isActive ? "ar-ref-badge--active" : "ar-ref-badge--off"}`}>
                        {ref.isActive ? "Aktivan" : "Neaktivan"}
                      </div>
                      <div className="ar-ref-stat-lab">Status</div>
                    </div>
                    <div className="ar-ref-stat ar-ref-stat--code">
                      <div className="ar-ref-code-pill">{ref.code}</div>
                      <div className="ar-ref-stat-lab">Kod</div>
                    </div>
                  </div>

                  {/* Reward progress */}
                  {ref.rewards && ref.rewards.length > 0 && (
                    <div className="ar-rw-progress-list">
                      <div className="ar-rw-progress-list-title">Nagrade</div>
                      {ref.rewards.map((rw, i) => (
                        <RewardProgress
                          key={i}
                          reward={rw}
                          totalBookings={ref.totalBookings}
                          totalNights={ref.totalNights}
                        />
                      ))}
                    </div>
                  )}

                  {/* No rewards hint */}
                  {(!ref.rewards || ref.rewards.length === 0) && (
                    <button
                      className="ar-rw-no-rewards-hint"
                      onClick={() => setEditingId(ref._id)}
                    >
                      <PlusOutlined style={{ fontSize: 11 }} /> Dodaj nagrade za referera
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferralsTab;
